import checkTaskTemplateAndThemeForClasses from '@/api/classes/checkTaskTemplateAndThemeForClasses.endpoint';
import getTeachersClasses from '@/api/classes/getTeachersClasses.endpoint';
import enableThemesAndActivities from '@/api/contentActivitiesLibrary/enableThemesAndActivities.endpoint';
import useCustomHook from '@/hooks/useCustomHook';
import openAPIErrorNotification from '@/shared/APIErrorNotification/APIErrorNotification';
import { GlobalContext } from '@/shared/GlobalContext/GlobalContext';
import { NavigationContext } from '@/shared/NavigationContext/NavigationContext';
import { notification } from 'antd';
import { useEffect, useContext } from 'react';
import { deepClone } from 'src/modules/objectUtils';

export default function useAddThemes({ themes, onFinish }) {
  const context = useContext(GlobalContext);
  const navigationContext = useContext(NavigationContext);

  const { state, actions } = useCustomHook({
    defaultState: {
      loading: true,
      classes: [],
      selectedClasses: [],
      processing: false,
      classesAlreadyAdded: [],
      selectedClassesAlreadyAdded: [],
      editorActive: false,
      customEnabledActivities: null,
    },
    actions: {
      loadClasses: async () => {
        actions.setState({
          loading: true,
        });
        try {
          let classes = [];
          if (context.state.isTeacherPro) {
            classes = await getTeachersClasses({
              teacherId: context.state.user?.teacher?.id,
            });
          }
          classes.push({
            id: context.state.user?.teacher?.internalUseClassId,
            name: 'Shared student account',
          });

          let classesAlreadyAdded = [];
          if (themes?.length == 1 && classes.length) {
            const [theme] = themes;
            const themeInClass = await checkTaskTemplateAndThemeForClasses(
              classes.map((clazz) => clazz.id),
              theme.id
            );
            classesAlreadyAdded = getClassesAlreadyAdded({
              classes,
              themeInClass,
            });
          }

          actions.setState({
            classes: classes.sort((a, b) => a.name.localeCompare(b.name)),
            loading: false,
            classesAlreadyAdded,
          });
        } catch (e) {
          console.log(e);
          actions.setState({ loading: false });
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem loading the classes',
            errorFeedback: e.feedbackData,
          });
        }
      },
      onClassAdded: (_, clazz) => {
        const selectedClasses = deepClone(state.selectedClasses);
        selectedClasses.push(clazz);
        actions.setState({ selectedClasses });
      },
      onClassRemoved: (classId) => {
        const selectedClasses = state.selectedClasses.filter(
          (clazz) => clazz.id != classId
        );
        actions.setState({ selectedClasses });
      },
      setEditorActive: (editorActive) => {
        actions.setState({ editorActive });
      },
      onCustomActivitiesSelected: (activities) => {
        actions.setState({ customEnabledActivities: activities });
      },
      save: async () => {
        actions.setState({
          processing: true,
        });
        try {
          const data = prepareData(
            state,
            themes,
            navigationContext.state.selectedTagId
          );
          await enableThemesAndActivities(data);
          actions.setState({
            processing: false,
          });
          notification.success({
            message: 'Themes added',
            top: 60,
          });
          onFinish();
        } catch (e) {
          actions.setState({ processing: false });
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem adding the themes',
            errorFeedback: e.feedbackData,
          });
        }
      },
    },
  });

  useEffect(() => {
    if (context.state.loaded && context.state.user?.teacher) {
      actions.loadClasses();
    }
  }, [context.state.loaded]);

  useEffect(() => {
    let selectedClasses = [];
    if (navigationContext.state.classes?.length) {
      selectedClasses = state.classes.filter((clazz) =>
        navigationContext.state.classes.some(
          (navigationClass) => navigationClass.id == clazz.id
        )
      );
    } else if (state.classes.length == 1) {
      selectedClasses = [state.classes[0]];
    }
    actions.setState({ selectedClasses });
  }, [navigationContext.state.classes, state.classes]);

  useEffect(() => {
    const selectedClassesAlreadyAdded = state.classesAlreadyAdded.filter(
      (classAlreadyAdded) =>
        state.selectedClasses.some(
          (selectedClass) => selectedClass.id == classAlreadyAdded.id
        )
    );
    actions.setState({ selectedClassesAlreadyAdded });
  }, [state.selectedClasses, state.classesAlreadyAdded]);

  useEffect(() => {
    if (!state.editorActive) {
      actions.setState({ customEnabledActivities: null });
    }
  }, [state.editorActive]);

  return { state, actions };
}

function prepareData(state, themes, tagId) {
  return {
    classIds: state.selectedClasses.map((selectedClass) => selectedClass.id),
    themes: themes.map((theme) => ({
      id: theme.id,
      enabledActivities: state.customEnabledActivities?.length
        ? state.customEnabledActivities
        : tagId
        ? theme.activities.map((activity) => activity.id)
        : null,
    })),
  };
}

function getClassesAlreadyAdded({ classes, themeInClass }) {
  return themeInClass?.classResults
    .filter((classResult) => classResult.themeEnabled)
    .map((classResult) =>
      classes.find((clazz) => clazz.id == classResult.classId)
    );
}
